import TYPES               from "@/core/services/Changelogs/types";

import Title_ru_RU         from "@/core/misc/decorators/Title_ru_RU";
import Title_ru_RU_Class   from "@/core/misc/decorators/Title_ru_RU_Class";
import serializeSimplified from "@/core/misc/decorators/SerializeSimplified";

import BaseEntity          from "@/core/misc/BaseEntity";
import Core                from "@/core/Core";
import DateSimple          from "@/core/types/DateSimple";
import Vehicle             from "@/domains/Vehicles/Vehicle";
import VehicleOwnershipType from "@/domains/Vehicles/VehicleOwnershipType";
import DriverFeedback      from "@/domains/Drivers/DriverFeedback";

@Title_ru_RU_Class('Водитель')                      // driver → Driver
class Driver extends BaseEntity {
  static Events = {
    Created: 'driver:created',
    Updated: 'driver:updated',
    Deleted: 'driver:deleted',
  } as const;

  /** Имя водителя — одной строкой, как ввёл пользователь */
  @Title_ru_RU('Имя', TYPES.STRING)
  public name: string;

  /** Госномер транспортного средства */
  @Title_ru_RU('Госномер', TYPES.STRING)
  public registrationNumber: string | null;

  /** Модель машины из справочника транспорта */
  @Title_ru_RU('Модель машины', TYPES.OBJECT)
  @serializeSimplified
  public vehicle: Vehicle | null;

  @Title_ru_RU('Форма владения транспортным средством', TYPES.OBJECT)
  @serializeSimplified
  public vehicleOwnershipType: VehicleOwnershipType | null;

  /** фронтовые поля — считаются на бэке, при сохранении водителя не учитываются */

  /** Количество оценок */
  public feedbackCount: number = 0;

  /** Средняя оценка без округления; null — оценок нет */
  public averageRate: number | null;

  /** Оценка водителя, поставленная текущим пользователем; null — ещё не оценивал */
  public feedback: DriverFeedback | null;

  /**
   * Избранный водитель текущего пользователя. Избранный у пользователя один или его нет.
   * Уходит на бэк вместе с водителем в upsert
   */
  public isFavorite: boolean = false;

  @Title_ru_RU('Телефон', TYPES.STRING)
  public phone: string | null;

  @Title_ru_RU('ИНН', TYPES.STRING)
  public inn: string | null;

  @Title_ru_RU('Номер водительского удостоверения', TYPES.STRING)
  public licenseNumber: string | null;

  /** Дата выдачи водительского удостоверения */
  public licenseIssueDate: DateSimple | null;

  /** Средняя оценка с количеством оценок для реестра и карточки, например «4,3 (12)» */
  get averageRateString(): string {
    if (Core.utils.isNullOrUndefined(this.averageRate) === true) {
      return '--';
    }
    else {
      const rate = this.averageRate.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

      return `${rate} (${this.feedbackCount})`;
    }
  }

  /**
   * Рейтинг под ФИО в выпадающих списках: звёзды, средняя оценка и количество оценок, например «★★★★☆ 4,3 (12)».
   * Звёзды текстом — полузвезды среди символов нет, поэтому средняя оценка округляется до целого.
   * Оценок нет — «--»
   */
  get description(): string {
    if (Core.utils.isNullOrUndefined(this.averageRate) === true) {
      return 'Нет отзывов';
    }
    else {
      const fullStarsCount = Math.round(this.averageRate);

      return '★'.repeat(fullStarsCount) + '☆'.repeat(5 - fullStarsCount) + ' ' + this.averageRateString;
    }
  }

  /**
   * Новый водитель из свободного ввода в селекте (VSuggestionSelectV2 с :type="Driver").
   * Создаётся при сохранении ЗТК; избранным не становится
   */
  static fromSuggestion(name: string): Driver {
    const driver = new this();

    driver.name = name;

    return driver;
  }

  static fromServer(data: any, driver = new Driver()): Driver {
    super.fromServer(data, driver);

    driver.licenseIssueDate     = Core.utils.hydrate(data.licenseIssueDate, DateSimple.fromServer);
    driver.vehicle              = Core.utils.hydrate(data.vehicle, Vehicle.fromServer);
    driver.vehicleOwnershipType = Core.utils.hydrate(data.vehicleOwnershipType, VehicleOwnershipType.fromServer);
    driver.feedback             = Core.utils.hydrate(data.feedback, DriverFeedback.fromServer);

    // обратную ссылку на водителя ставит сам водитель последним шагом
    if (Core.utils.isNullOrUndefined(driver.feedback) === false) {
      driver.feedback.driver = driver;
    }

    return driver;
  }
}

export default Driver;
